import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, OnInit, Optional, ViewChild } from "@angular/core";
import { TranslocoService } from "@ngneat/transloco";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Compbaser } from "../compbaser";
import { DEFAULT_PROTOCOL, DEFAULT_SERVER, isNullOrUndefOrEmpty } from "../../common";
import { FontLoaderService, LocalStoreService, StoreSelectorsService } from "../../services";
import { actionSceneFontsPreviewed } from "../../store/actions/user.actions";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { faRectangleXmark } from "@fortawesome/free-solid-svg-icons";
import html2canvas from "html2canvas";
import _ from "lodash";

export interface IFontPreview {
    id: number;
    name: string;
    loaded: boolean;
    url: string;
    favorite: boolean;
}

export interface UserData {
    id: string;
    name: string;
}

@Component({
    selector: "app-font-preview",
    templateUrl: "./font-preview.component.html",
    styleUrls: ["./font-preview.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FontPreviewComponent extends Compbaser implements OnInit, AfterViewInit {
    readonly faRectangleTimes = faRectangleXmark;
    m_fontScene: Array<IFontPreview> = [];
    totalFontsToPreview = 0;
    fontsPreviewed: Array<string> = [];
    httpRequests = [];
    isShowFavorite = false;
    allFontsDownloaded = false;
    favoriteFonts: Array<string> = [];
    displayedColumns = ["id", "name", "favorite"];
    dataSource: MatTableDataSource<IFontPreview>;
    selectedFont;
    fontFilter = "";

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    onSelectedFont = new EventEmitter();
    showFavoritesOnly: any = false;
    previewText = "The quick brown fox jumps over the lazy dog";
    previewFontSize = 18;

    constructor(
        private translocoService: TranslocoService,
        @Optional() @Inject(MAT_DIALOG_DATA) data,
        private fontLoaderService: FontLoaderService,
        private localStoreService: LocalStoreService,
        public dialogRef: MatDialogRef<FontPreviewComponent>,
        public storeSelector: StoreSelectorsService,
        private cd: ChangeDetectorRef,
    ) {
        super();
        this.cod(
            this.storeSelector.listenFontsPreviewed().subscribe((v) => {
                this.fontsPreviewed = _.cloneDeep(v); // Need cloneDeep - array from store is frozen/non-extensible
                this.cd.markForCheck(); // Trigger change detection with OnPush
            }),
        );
    }

    ngOnInit() {
        const fonts = _.cloneDeep(this.fontLoaderService.getFontLibraryUnmodified()); // Need cloneDeep - object is frozen/non-extensible
        this.totalFontsToPreview = Object.keys(fonts).length; // Use Object.keys().length instead of _.size
        const favorites = this.localStoreService.getStorageValConfig("favoriteFonts");
        if (isNullOrUndefOrEmpty(favorites)) {
            this.favoriteFonts = [];
        } else {
            this.favoriteFonts = favorites.split(",");
        }

        // Cache toLowerCase for performance
        const favoritesSet = new Set(this.favoriteFonts); // Use Set for O(1) lookup instead of indexOf

        Object.keys(fonts)
            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
            .forEach((font, b) => {
                const s = `${fonts[font][0]}`.split(",")[0];
                const u = font.replace(/[\s\[\]\-!•™&:.,()`'?]/g, "");
                const favorite = favoritesSet.has(font); // O(1) lookup with Set
                const fontPreview: IFontPreview = { id: b, name: font, loaded: false, url: u, favorite };
                this.m_fontScene.push(fontPreview);
                if (b < 11) this.loadFont(fontPreview);
            });
        this.dataSource = new MatTableDataSource(this.m_fontScene);

        this.dataSource.filterPredicate = (data: IFontPreview, filter: string) => {
            if (!data || !data.name) return false; // Safety check
            if (!filter) return true; // No filter, show all
            const filters = JSON.parse(filter);
            const nameMatch = data.name.toLowerCase().includes(filters.filterValue);

            if (filters.showFavoritesOnly) {
                return data.favorite && nameMatch;
            }
            return nameMatch;
        };

        // Initialize filter to prevent undefined errors
        this.dataSource.filter = JSON.stringify({
            filterValue: '',
            showFavoritesOnly: false,
        });
    }

    loadFont(fontObject) {
        if (this.fontsPreviewed.length == this.totalFontsToPreview) {
            this.allFontsDownloaded = true;
        }
        if (this.fontsPreviewed.indexOf(fontObject.url) > -1) return;
        this.fontsPreviewed.push(fontObject.url);
        const url = `${DEFAULT_PROTOCOL}${DEFAULT_SERVER}/TTF/${fontObject.url}.ttf`;
        const request = new XMLHttpRequest();
        this.httpRequests.push(request);
        request.addEventListener("readystatechange", (e) => {
            if (request.readyState == 2 && request.status == 200) {
                // Download is being started
            } else if (request.readyState == 3) {
                // Download is under progress
            } else if (request.readyState == 4) {
                // Downloading has finished
                // request.response holds the binary data of the font
                // this.m_fontScene[fontObject.url].loaded = true;
                // console.log('loading ' + url);
                const junction_font = new FontFace(`${fontObject.name}`, `url(${url})`);
                junction_font
                    .load()
                    .then((loaded_face) => {
                        document.fonts["add"](loaded_face);
                        document.body.style.fontFamily = `"${fontObject.name}"`;
                    })
                    .catch((error) => {
                        console.log("err loading font " + url);
                    });
            }
        });
        request.addEventListener("progress", (e) => {
            // const percent_complete = (e.loaded / e.total) * 100;
            // console.log(percent_complete);
        });
        request.responseType = "arraybuffer";
        request.open("get", url);
        request.send();

        /** a more raw way of downloading fonts for reference **/
        // this.playerService = this.injector.get('playerService');
        // const promise: Promise<any> = this.playerService.caching['getUrl'](url);
        // promise.then((typeFace) => {
        //   console.log(typeFace);
        // });
        // const ff = new FontFace(`${fontName}`, `url(${url})`);
        // ff.load().then((loaded_face) => {
        //   document.fonts['add'](loaded_face);
        //   document.body.style.fontFamily = `${fontStyle}`;
        //   alert('loaded font');
        // }).catch((error) => {
        //   alert('problem loading font ' + fontName);
        // });
    }

    onCaptureFontStyle(row) {
        const name = row.name;
        const element = document.getElementById(name) as HTMLElement;
        this.onCapture(element, name);
        return;
    }

    onCapture(element, name) {
        html2canvas(element).then((canvas) => {
            canvas.toBlob((blob) => {
                const link = document.createElement("a");
                link.download = `${name}.png`;
                link.href = URL.createObjectURL(blob);
                link.click();
                return;
            }, "image/png");
        });
    }

    toggleFavorite(row: IFontPreview) {
        // Toggle favorite directly on the row object
        row.favorite = !row.favorite;

        // Build favorites list using filter instead of forEach
        const saveFavoriteFonts = this.dataSource.data
            .filter(v => v.favorite)
            .map(v => v.name);

        if (saveFavoriteFonts.length > 0) {
            this.localStoreService.setStorageValConfig("favoriteFonts", saveFavoriteFonts.join());
        } else {
            // Clear favorites if none selected
            this.localStoreService.setStorageValConfig("favoriteFonts", "");
        }

        // Trigger change detection for OnPush
        this.cd.markForCheck();
    }

    loadPage(i_page) {
        const pagedOutput = [];
        let itemIndex = 0;
        this.dataSource.data.forEach((element) => {
            if (itemIndex >= this.paginator.pageIndex * this.paginator.pageSize && itemIndex < (this.paginator.pageIndex + 1) * this.paginator.pageSize) {
                pagedOutput.push(element);
                this.loadFont(element);
            }
            itemIndex++;
        });
    }

    onSelectedFontPreview(i_font: IFontPreview) {
        this.selectedFont = i_font;
        this.onSelectedFont.emit(i_font);
        this.cd.markForCheck(); // Trigger change detection for OnPush
    }

    cancelFontDownloads() {
        this.storeSelector.dispatch(actionSceneFontsPreviewed({ fonts: this.fontsPreviewed }));
        this.httpRequests.forEach((v) => {
            v.abort();
        });
    }

    /**
     * Set the paginator and sort after the view init since this component will
     * be able to query its view for the initialized paginator and sort.
     */
    ngAfterViewInit() {
        if (this.dataSource) {
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
        }
    }

    applyFilter() {
        if (!this.dataSource) return; // Safety check
        this.dataSource.filter = JSON.stringify({
            filterValue: (this.fontFilter || '').trim().toLowerCase(),
            showFavoritesOnly: this.showFavoritesOnly,
        });
        this.dataSource.filteredData.forEach((i_font) => {
            this.loadFont(i_font);
        });
        this.cd.markForCheck(); // Trigger change detection for OnPush
    }

    clearSelection() {
        this.fontFilter = "";
        this.applyFilter();
    }

    toggleShowFavorites() {
        this.showFavoritesOnly = !this.showFavoritesOnly;
        this.applyFilter();
    }

    /**
     * TrackBy function for ngFor to improve rendering performance
     */
    trackByFontId(index: number, item: IFontPreview): number {
        return item.id;
    }

    onClose() {
        this.dialogRef.close();
        this.cancelFontDownloads();
    }
}
